import React, { useState } from 'react';
import { ArrowLeft, FileDown, Send, MoreHorizontal, RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import PublishBriefModal from './PublishBriefModal';
import { toast } from 'sonner';

// ──────────────────────────────────────────────────────────────────────
// CaseWorkspaceTopStripe — visual pass matching the reference.
//   • Green "Back to queue" link on the left.
//   • Title "Concierge case" (large, dark) + "#CC-XXXX · Manual" sub-line.
//   • Right cluster: Preview (outline · Soon), Send to customer (purple
//     soft · Soon), kebab (Soon), Update status (green primary).
// ──────────────────────────────────────────────────────────────────────

const GREEN = '#0F5132';
const PURPLE = '#5B47E8';

const STATUS_LABELS = {
  new: 'New',
  claimed: 'Claimed',
  reviewing: 'Reviewing',
  researching: 'Researching',
  waiting_customer: 'Waiting on customer',
  waiting_vendor: 'Waiting on vendor',
  recommendations_sent: 'Recommendations sent',
  followup_needed: 'Follow-up needed',
  closed: 'Closed',
};

const SETTABLE_STATUS_OPTIONS = [
  'claimed',
  'reviewing',
  'researching',
  'waiting_customer',
  'waiting_vendor',
  'recommendations_sent',
  'followup_needed',
];

// Compact 6-char hash — reproducible per event_id.
const shortCaseId = (eventId) => {
  if (!eventId) return '------';
  const flat = String(eventId).replace(/-/g, '');
  return flat.slice(0, 6).toUpperCase();
};

export default function CaseWorkspaceTopStripe({
  onBack,
  eventId,
  status,
  statusSaving,
  onStatusChange,
  isClosed,
  publishStatus,
  onPublish,
  onRefetchPublishStatus,
  onOpenPdfPreview,
  assignedWorker,
}) {
  const [kebabOpen, setKebabOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const caseNumber = `#CC-${shortCaseId(eventId)}`;
  const statusLabel = STATUS_LABELS[status] || 'New';

  const published = !!publishStatus?.published;
  const hasChanges = !!publishStatus?.has_unpublished_changes;

  // Button label + affordance: mirrors user spec Commit 3 · Q2.
  let sendLabel;
  let sendIcon;
  if (!published) {
    sendLabel = 'Send to customer';
    sendIcon = <Send className="w-4 h-4" aria-hidden />;
  } else if (hasChanges) {
    sendLabel = 'Republish updates';
    sendIcon = <RefreshCw className="w-4 h-4" aria-hidden />;
  } else {
    sendLabel = 'Published';
    sendIcon = <CheckCircle2 className="w-4 h-4" aria-hidden />;
  }

  const openPublishModal = async () => {
    // Fresh check right before opening — draft may have changed since
    // last poll. Best-effort.
    if (onRefetchPublishStatus) {
      try { await onRefetchPublishStatus(); } catch (_) { /* noop */ }
    }
    setPublishModalOpen(true);
  };

  const handlePdfPreview = async () => {
    if (!onOpenPdfPreview || pdfLoading) return;
    setPdfLoading(true);
    try {
      await onOpenPdfPreview();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not open PDF preview.';
      toast.error(detail);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      className="border-b border-[#EFE3CE] bg-[#FFF7EE]"
      data-testid="case-workspace-top-stripe"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
        {/* Left cluster — back link + title block */}
        <div className="flex items-start gap-5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-medium hover:opacity-80 transition-opacity shrink-0"
            style={{ color: GREEN }}
            data-testid="case-workspace-back-to-queue"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden />
            Back to queue
          </button>
          <div className="min-w-0">
            <h1
              className="text-[22px] sm:text-[24px] md:text-[26px] font-semibold leading-none truncate"
              style={{ color: '#111827' }}
              data-testid="case-workspace-title"
            >
              Concierge case
            </h1>
            <div className="mt-1.5 text-[12.5px] flex items-center gap-2 flex-wrap">
              <span className="font-medium" style={{ color: GREEN }} data-testid="case-workspace-case-number">
                {caseNumber}
              </span>
              <span className="text-gray-400" aria-hidden>·</span>
              <span className="text-gray-500" data-testid="case-workspace-status-manual">Manual</span>
              {/* B-EXP-6.24 — ownership indicator. Low-noise chip
                  driven by the existing `assigned_worker_id` /
                  `assigned_worker.display_name` from the workspace
                  snapshot; no second assignment field created. Hidden
                  when unclaimed so the stripe stays quiet. */}
              {assignedWorker ? (
                <>
                  <span className="text-gray-400" aria-hidden>·</span>
                  <span
                    className="inline-flex items-center gap-1 text-[12px] text-gray-600"
                    data-testid="case-workspace-assigned-to"
                    data-assigned-worker-id={assignedWorker.id || ''}
                  >
                    <span className="text-gray-500">Assigned to</span>
                    <span className="font-medium text-gray-800">
                      {assignedWorker.display_name || 'Former teammate'}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right cluster — actions */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            disabled={pdfLoading}
            title="Download or open the customer-facing brief as PDF."
            onClick={handlePdfPreview}
            className="gap-1.5 border-[#E8DEC7] text-gray-700 hover:bg-[#FAF6EE] bg-white"
            data-testid="case-action-pdf-preview"
          >
            {pdfLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            ) : (
              <FileDown className="w-4 h-4" aria-hidden />
            )}
            {pdfLoading ? 'Opening…' : 'PDF preview'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            disabled={isClosed}
            title={
              isClosed
                ? 'Reopen the case to publish updates.'
                : (published && !hasChanges
                    ? 'Already published — click to view or copy the link.'
                    : (published ? 'Republish updates for the customer.' : 'Publish and get a shareable customer link.'))
            }
            onClick={openPublishModal}
            className="gap-1.5 border-[#DFD5FF] hover:bg-[#F3EFFF]"
            style={{ backgroundColor: '#F3EFFF', color: PURPLE }}
            data-testid="publish-brief-btn"
          >
            {sendIcon}
            {sendLabel}
          </Button>

          <Popover open={kebabOpen} onOpenChange={setKebabOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-white border border-transparent hover:border-[#E8DEC7]"
                aria-label="More actions"
                data-testid="case-action-kebab"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              {/* B-EXP-6.19 Defect 3 — Log outreach moved out of the primary
                  Case Actions card into this overflow menu. Backend
                  endpoints /workspace/{id}/outreach + reach_out_vendor
                  remain in place; the UI is still Soon so the endpoint's
                  future consumer has a stable home. */}
              <button
                type="button"
                disabled
                className="w-full text-left px-3 py-2 text-sm text-gray-400 rounded-md cursor-not-allowed"
                title="Wired in a later step"
                data-testid="case-kebab-log-outreach"
              >
                Log outreach (Soon)
              </button>
              <button
                type="button"
                disabled
                className="w-full text-left px-3 py-2 text-sm text-gray-400 rounded-md cursor-not-allowed"
                title="Wired in a later step"
                data-testid="case-kebab-transfer"
              >
                Transfer case (Soon)
              </button>
            </PopoverContent>
          </Popover>

          {/* Update status — green primary. Wired to existing status change. */}
          <Select
            open={statusMenuOpen}
            onOpenChange={setStatusMenuOpen}
            value={status || undefined}
            onValueChange={onStatusChange}
            disabled={statusSaving || isClosed}
          >
            <SelectTrigger
              className="gap-1.5 text-white border-transparent hover:opacity-90 w-auto pl-4 pr-3 py-2 h-auto rounded-md text-[13px] font-medium shadow-sm"
              style={{ backgroundColor: GREEN }}
              data-testid="case-status-trigger"
            >
              <SelectValue placeholder="Update status">
                {statusMenuOpen ? 'Update status' : statusLabel}
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="end" data-testid="case-status-menu">
              {SETTABLE_STATUS_OPTIONS.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`case-status-option-${s}`}
                >
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <PublishBriefModal
        open={publishModalOpen}
        onOpenChange={setPublishModalOpen}
        publishStatus={publishStatus}
        onConfirm={onPublish}
      />
    </div>
  );
}
